import { useCallback, useEffect, useRef } from 'react';

import { useAppSelect } from '~/app/store';
import type { PeripheralType } from '~/common/models/peripheral.model';

import { ONSTAR_PRODUCT_NAME } from './activationProgress.types';
import { selectLastActivationCompletedAt } from './state/activationProgressSlice.selectors';
import {
  type StartActivationOptions,
  type UseStartActivationArgs,
  useStartActivation,
} from './useStartActivation';

export type UseActivationListRefreshArgs = UseStartActivationArgs & {
  /** Invoked whenever any activation batch reaches a terminal state. */
  onActivationComplete: () => void;
  /**
   * Gates only the global completion listener. When false, batch completions no
   * longer trigger `onActivationComplete` (the returned `activate` still works),
   * so a consumer mounted unconditionally for React's rules-of-hooks doesn't
   * refetch on unrelated activations. Defaults to true.
   */
  enabled?: boolean;
};

/** Per-call options forwarded to `useStartActivation`, sans the fixed product name. */
export type ActivateOptions = Omit<StartActivationOptions, 'productName'>;

export type UseActivationListRefreshResult = {
  /**
   * Starts an OnStar activation batch for the given peripherals; the
   * global progress widget takes over tracking and completion reporting.
   *
   * Resolves `true` when activation started/completed and `false` on a submit
   * failure, so modal callers can keep the dialog open and show an inline
   * payment error (pass `{ suppressFailureToast: true }` to skip the toast).
   */
  activate: (
    peripheralIds: string[],
    peripheralType: PeripheralType,
    options?: ActivateOptions
  ) => Promise<boolean>;
};

/**
 * Shared wiring for the peripheral device lists (cameras / adapters): starts an
 * activation batch and refreshes the list once activation completes so
 * activated devices drop out of the "activation required" view. Consolidates
 * the identical `useStartActivation` + completion-refetch logic each list used
 * to duplicate.
 */
export const useActivationListRefresh = ({
  organizationId,
  fleetId,
  onActivationComplete,
  enabled = true,
}: UseActivationListRefreshArgs): UseActivationListRefreshResult => {
  const { start } = useStartActivation({ organizationId, fleetId });
  const lastActivationCompletedAt = useAppSelect(
    selectLastActivationCompletedAt
  );

  // Hold the latest callback so the completion effect only depends on the
  // batch-completion marker, not the caller's callback identity.
  const onCompleteRef = useRef(onActivationComplete);
  useEffect(() => {
    onCompleteRef.current = onActivationComplete;
  }, [onActivationComplete]);

  // Read `enabled` at fire time (via ref) so toggling it never re-fires the
  // completion callback — the effect still runs only on a new completion.
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (lastActivationCompletedAt === undefined) return;
    if (!enabledRef.current) return;
    onCompleteRef.current();
  }, [lastActivationCompletedAt]);

  const activate = useCallback(
    (
      peripheralIds: string[],
      peripheralType: PeripheralType,
      options?: ActivateOptions
    ): Promise<boolean> =>
      start(peripheralIds, peripheralType, {
        productName: ONSTAR_PRODUCT_NAME,
        ...options,
      }),
    [start]
  );

  return { activate };
};
